import { Box } from '@mui/material';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import Form from '@product-framework/components/AdvancedForms/Form';
import {
  ButtonContainer,
  IconLibrary,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import { observer } from 'mobx-react';
import { useCallback, useMemo, useRef } from 'react';
import schema from './edit.json';
import validationSchema from './validations';
import {
  fetchDispatchDetails,
  fetchBasicDetails,
  submitDispatchDetails,
  submitBasicDetails,
} from './lib';
import getSPLevelStackableButtons from '../../../utils/getSPLevelStackableButtons';
import ServiceProviderLayout from '../../../components/ServiceProviderLayout';
import {
  useServiceProvider,
  withServiceProvider,
} from '../../../../../providers/ServiceProviderProvider';

const FORM_ID = 'ServiceProviderDispatchSettingsForm';
const PERMISSION_SP = 'SERVICEPROVIDERPROFILE';

function DispatchSettings() {
  const navigate = useNavigate();
  const { serviceProviderId, refreshData } = useServiceProvider();
  const { formatMessage } = useLocalization();
  const formRef = useRef();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl, put } = useDataProvider(provider);

  const commonParams = useMemo(
    () => ({
      get,
      getApiUrl,
      put,
      serviceProviderId,
    }),
    [get, getApiUrl, put, serviceProviderId],
  );

  const onChange = () => {
    refreshData({
      refreshProfileWarnings: true,
    });
  };

  const fetchDefaultValues = useCallback(async () => {
    const [responseDispatchDetails, responseFetchBasic] = await Promise.all([
      fetchDispatchDetails(commonParams),
      fetchBasicDetails(commonParams),
    ]);

    let initialValues = {};
    let errors = [];
    let warnings = [];
    if (responseDispatchDetails?.ok && responseDispatchDetails.data) {
      warnings = [
        ...warnings,
        ...(responseDispatchDetails.data?.warnings || []),
      ];

      const dispatchInitialValues = {
        dispatchMethod: responseDispatchDetails.data?.dispatchMethod || '',
        contractorComplianceStatus:
          responseDispatchDetails.data?.contractorComplianceStatus || '',
        plusoneExternalReferenceId:
          responseDispatchDetails.data?.plusoneExternalReferenceId || '',
      };
      initialValues = {
        ...dispatchInitialValues,
      };
    } else {
      errors = [...(responseDispatchDetails?.errors || [])];
    }

    if (responseFetchBasic?.ok && responseFetchBasic.data) {
      warnings = [...warnings, ...(responseFetchBasic.data?.warnings || [])];

      const basicInitialValues = {
        serviceProviderType: {
          label: responseFetchBasic?.data?.serviceProviderType?.typeName,
          value: responseFetchBasic?.data?.serviceProviderType?.id,
        },
      };
      initialValues = {
        ...initialValues,
        ...basicInitialValues,
      };
    } else {
      errors = [...(responseFetchBasic?.errors || [])];
    }

    if (errors?.length > 0) {
      const result = {
        data: initialValues,
        errors,
        warnings,
      };
      return result;
    }

    const result = {
      data: initialValues,
      errors: null,
      warnings,
    };
    return result;
  }, [commonParams]);

  const onSubmit = async (data, additional, permissions) => {
    let updatedPageData = data;
    let errors = [];
    let warnings = [];
    const defaultValues = formRef.current.getDefaultValues();

    const [responseDispatchDetails, responseBasicDetails] = await Promise.all([
      submitDispatchDetails(commonParams, data, permissions),
      submitBasicDetails(commonParams, data, defaultValues),
    ]);

    if (responseDispatchDetails?.ok && responseDispatchDetails.data) {
      warnings = [
        ...warnings,
        ...(responseDispatchDetails.data?.warnings || []),
      ];

      const dispatchInitialValues = {
        dispatchMethod: responseDispatchDetails.data?.dispatchMethod || '',
        contractorComplianceStatus:
          responseDispatchDetails.data?.contractorComplianceStatus || '',
        plusoneExternalReferenceId:
          responseDispatchDetails.data?.plusoneExternalReferenceId || '',
      };
      updatedPageData = {
        ...dispatchInitialValues,
      };
    } else {
      errors = [...(responseDispatchDetails?.errors || [])];
    }

    if (responseBasicDetails?.ok && responseBasicDetails.data) {
      warnings = [...warnings, ...(responseBasicDetails.data?.warnings || [])];

      const basicInitialValues = {
        serviceProviderType: {
          label: responseBasicDetails?.data?.serviceProviderType?.typeName,
          value: responseBasicDetails?.data?.serviceProviderType?.id,
        },
      };
      updatedPageData = {
        ...updatedPageData,
        ...basicInitialValues,
      };
    } else {
      errors = [...(responseBasicDetails?.errors || [])];
    }

    if (errors?.length > 0) {
      return {
        nextValues: data,
        errors,
        warnings,
      };
    }
    snackbar.showMessage({
      type: 'success',
      data: formatMessage({
        id: 'DISPATCH_SYSTEM_SETTINGS_WERE_SAVED_SUCCESSFULLY',
        defaultMessage: 'Dispatch System settings were saved successfully',
      }),
    });
    return {
      nextValues: updatedPageData,
      errors: null,
      warnings,
      callback: () => {
        onChange();
      },
    };
  };

  return (
    <ServiceProviderLayout
      selectedStepperId="SYSTEM_SETTINGS_DISPATCH"
      stackablePageButtons={getSPLevelStackableButtons({
        navigate,
      })}>
      <PageSection
        label={
          <FormattedMessage
            id="SYSTEM_SETTINGS_DISPATCH"
            defaultMessage="System Settings - Dispatch"
          />
        }>
        <Box sx={{ mt: 2 }}>
          <Form
            formId={FORM_ID}
            ref={formRef}
            permissionEntities={[PERMISSION_SP]}
            onSubmit={onSubmit}
            schema={schema}
            fetchDefaultValues={fetchDefaultValues}
            validationSchema={validationSchema(formatMessage)}>
            <ButtonContainer>
              <PermissionButton
                permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_DISPATCH"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => formRef.current?.submit({ formId: FORM_ID })}>
                <FormattedMessage
                  id="SAVE_DISPATCH_SETTINGS"
                  defaultMessage="Save Dispatch Settings"
                />
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_UPDATE_SP_MODULE_SETTINGS_DISPATCH"
                variant="contained"
                startIcon={<IconLibrary.Reset />}
                color="primary"
                onClick={() => formRef.current?.reset()}>
                <FormattedMessage id="RESET" defaultMessage="Reset" />
              </PermissionButton>
            </ButtonContainer>
          </Form>
        </Box>
      </PageSection>
    </ServiceProviderLayout>
  );
}

const DispatchSettingsObserver = observer(DispatchSettings);
export default withServiceProvider(DispatchSettingsObserver);
